// @flow

import * as React from 'react';
import { graphql, useFragment } from 'react-relay';
import { Box, useSystem } from 'react-system';
import { Button, ButtonBase } from '@material-ui/core';
import { Link } from '../../controls/link';
import * as icons from '../../icons';
import { useLocale } from '../../hooks/locale';
import { useTheme } from '../../theme';
import { createDescriptor } from '../../controls/router';
import { getPlaceTitle, getPlaceSubTitle } from '../translate/place';
import {
  placesDescriptor,
  offerTypeDescriptor,
  roomsFilterDescriptor,
} from './Filters/sharedDescriptors';
import { propertyTypeFilterDescriptor } from './Filters';
import {
  propertyTypeSEOChoicesBuy,
  propertyTypeSEOChoicesRent,
} from './Filters/PropertyMainTypesFilter';

import type { AggregatesListingsSEOLinks_place$key } from './__generated__/AggregatesListingsSEOLinks_place.graphql.js';
import type { AggregatesListingsSEOLinks_popularSearches$key } from './__generated__/AggregatesListingsSEOLinks_popularSearches.graphql.js';

import type { PlaceData } from './useAggregatesListingsPlaceData';

type Props = {|
  popularSearches: AggregatesListingsSEOLinks_popularSearches$key,
  place: ?AggregatesListingsSEOLinks_place$key,
  placeData: PlaceData,
  className?: string,
  css?: any,
  showSEOText?: boolean,
  popularSearchSEOtext?: string,
  flushLinks?: boolean,
  showOnlyPopularSearches?: boolean,
  hideExternalAppLinks?: boolean,
|};

const THREE_COLUMNS_WIDTH = 250;
const TWO_COLUMNS_WIDTH = 288;

const descriptor = createDescriptor(
  placesDescriptor,
  propertyTypeFilterDescriptor,
  offerTypeDescriptor,
  roomsFilterDescriptor,
);

const numberOfRoomsToQuery = n => {
  if (n === 6) {
    return { numberOfRooms_gte: 6, numberOfRooms_lte: null };
  }
  return { numberOfRooms_gte: n, numberOfRooms_lte: n + 0.5 };
};

const isModifiedEvent = (event: SyntheticMouseEvent<>): boolean => {
  return (
    event.metaKey || event.ctrlKey || event.shiftKey || event.altKey // triggers resource download
  );
};

const Links = ({ title, items }) => {
  const { colors, text } = useTheme();

  const TABLE_ROW_HEIGHT = 22;
  const ROWS_WHEN_COLLAPSED = 6;

  const [itemsExpanded, itemsToggle] = React.useReducer(show => !show, false);
  const itemsToShow = itemsExpanded
    ? items.length
    : Math.min(items.length, ROWS_WHEN_COLLAPSED);

  const itemsHeight = itemsToShow * TABLE_ROW_HEIGHT + (itemsExpanded ? 0 : 1);

  return (
    items.length !== 0 && (
      <Box width={1}>
        <Box as="h3" pt={3} pb={2} css={[text.subtitle1]}>
          {title}
        </Box>
        <div
          css={{
            position: 'relative',
            overflow: 'hidden',
            height: itemsHeight,
            transition: 'height .5s',
          }}
        >
          <div
            css={{
              position: 'absolute',
              width: '100%',
              overflow: 'auto',
            }}
          >
            {items.map((item, i) => (
              <Link key={i} href={item.href}>
                <a
                  css={[
                    text.body2,
                    text.ellipsis,
                    {
                      cursor: 'pointer',
                      display: 'block',
                      textDecoration: 'none',
                      color: colors.mediumText,
                      ':hover': {
                        color: colors.primaryMain,
                      },
                    },
                  ]}
                  onClick={e => {
                    if (!isModifiedEvent(e)) {
                      item.onClick();
                    }
                  }}
                >
                  {item.label}
                </a>
              </Link>
            ))}
          </div>
        </div>
        {ROWS_WHEN_COLLAPSED < items.length && (
          <LinksButton
            onClick={() => itemsToggle(itemsExpanded)}
            expanded={itemsExpanded}
          />
        )}
      </Box>
    )
  );
};

const LinksButton = ({ onClick, expanded }) => {
  const { t } = useLocale();
  const { text, colors } = useTheme();
  return (
    <ButtonBase
      onClick={onClick}
      css={[text.caption, { cursor: 'pointer', colors: colors.primaryMain }]}
    >
      {expanded ? t('showLess') : t('showMore')}
      <icons.ExpandMore
        size={14}
        css={{
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform .2s',
        }}
      />
    </ButtonBase>
  );
};

const AggregatesListingsSEOLinksInner = props => {
  const popularSearches = useFragment(
    graphql`
      fragment AggregatesListingsSEOLinks_popularSearches on AggregatorCustomPage
      @relay(plural: true) {
        h1
        slug
      }
    `,
    props.popularSearches,
  );

  const place = useFragment(
    graphql`
      fragment AggregatesListingsSEOLinks_place on Place {
        type
        neighbouringAlternatives {
          label
          inLabel
          type
          slug
          lang
        }
        innerAlternatives {
          label
          inLabel
          type
          slug
          lang
        }
      }
    `,
    props.place,
  );

  const { placeData } = props;

  const innerPlaces = place?.innerAlternatives ?? [];
  const neighbouringPlaces = place?.neighbouringAlternatives ?? [];

  const popularSearchesItems =
    popularSearches && popularSearches.length > 0
      ? popularSearches.map(search => ({
          label: search.h1,
          href: { pathname: '/popular-searches', query: { slug: search.slug } },
          onClick: () => {},
        }))
      : [];

  const { t } = useLocale();
  const { text, maxWidths } = useTheme();
  const { media } = useSystem();

  const [descriptorState, setDescriptorState, flush] = descriptor.useState();
  const placeSlugs = descriptorState.placeSlugs ?? [];
  const slug = placeSlugs.length === 1 ? placeSlugs[0].slug : null;

  const alternatives = {
    innerPlaces: innerPlaces.map(subPlace => ({
      label: getPlaceTitle(
        t,
        {
          ...placeData,
          label: subPlace.label,
          inLabel: subPlace.inLabel,
          placeType: subPlace.type,
          rooms: null,
        },
        'within',
      ),
      href: {
        pathname: '/listings',
        query: descriptor.getQueryParams({
          ...descriptorState,
          numberOfRooms_gte: null,
          numberOfRooms_lte: null,
          placeSlugs: [{ slug: subPlace.slug, lang: subPlace.lang }],
        }),
      },
      onClick: () => {
        setDescriptorState({
          ...descriptorState,
          numberOfRooms_gte: null,
          numberOfRooms_lte: null,
          placeSlugs: [{ slug: subPlace.slug, lang: subPlace.lang }],
        });
        if (props.flushLinks !== false) {
          flush();
        }
      },
    })),
    neighbouringPlaces: neighbouringPlaces.map(subPlace => ({
      label: getPlaceTitle(
        t,
        {
          ...placeData,
          label: subPlace.label,
          inLabel: subPlace.inLabel,
          placeType: subPlace.type,
          rooms: null,
        },
        'within',
      ),
      href: {
        pathname: '/listings',
        query: descriptor.getQueryParams({
          ...descriptorState,
          numberOfRooms_gte: null,
          numberOfRooms_lte: null,
          placeSlugs: [{ slug: subPlace.slug, lang: subPlace.lang }],
        }),
      },
      onClick: () => {
        setDescriptorState({
          ...descriptorState,
          numberOfRooms_gte: null,
          numberOfRooms_lte: null,
          placeSlugs: [{ slug: subPlace.slug, lang: subPlace.lang }],
        });
        if (props.flushLinks !== false) {
          flush();
        }
      },
    })),
    propertyTypes: (descriptorState.offerType === 'buy'
      ? propertyTypeSEOChoicesBuy(t)
      : propertyTypeSEOChoicesRent(t)
    )
      .filter(({ value }) => value !== placeData.propertyType)
      .map(({ value }) => ({
        label: getPlaceTitle(
          t,
          { ...placeData, propertyType: value, rooms: null },
          'within',
        ),
        href: {
          pathname: '/listings',
          query: descriptor.getQueryParams({
            ...descriptorState,
            numberOfRooms_gte: null,
            numberOfRooms_lte: null,
            compositePropertyType_eq: value,
          }),
        },
        onClick: () => {
          setDescriptorState({
            ...descriptorState,
            numberOfRooms_gte: null,
            numberOfRooms_lte: null,
            compositePropertyType_eq: value,
          });
          if (props.flushLinks !== false) {
            flush();
          }
        },
      })),
    rooms:
      placeData.seoRoomLinksMinRooms == null
        ? []
        : range(placeData.seoRoomLinksMinRooms, 6).map(otherRooms => {
            const query = numberOfRoomsToQuery(otherRooms);
            const rooms = {
              lte: query.numberOfRooms_lte,
              gte: query.numberOfRooms_gte,
              noIndex: false,
            };
            return {
              label: getPlaceTitle(t, { ...placeData, rooms }, 'within'),
              href: {
                pathname: '/listings',
                query: descriptor.getQueryParams({
                  ...descriptorState,
                  ...query,
                }),
              },
              onClick: () => {
                setDescriptorState({ ...descriptorState, ...query });
                if (props.flushLinks !== false) {
                  flush();
                }
              },
            };
          }),
  };

  if (
    alternatives.neighbouringPlaces.length === 0 &&
    alternatives.propertyTypes.length === 0
  ) {
    return null;
  }

  const headerText =
    props.popularSearchSEOtext ?? getPlaceSubTitle(t, placeData);

  let pricesHref = null;

  switch (placeData.placeType) {
    case 'country':
      pricesHref = {
        pathname: `/place-prices`,
        query: {},
      };
      break;
    case 'state':
    case 'municipality':
    case 'locality':
    case 'neighbourhood':
      pricesHref = {
        pathname: `/place-prices`,
        query: {
          slug,
        },
      };
      break;
    default:
      break;
  }
  return (
    <Box
      as="section"
      width={1}
      mx="auto"
      pt={3}
      pb={4}
      className={props.className}
      css={media({
        maxWidth: maxWidths,
      })}
    >
      <Box as="header" css={{ textAlign: 'center', paddingBottom: '32px' }}>
        <Box as="h2" pb={3} css={[media([text.h5, text.h4])]}>
          {headerText}
        </Box>
        {pricesHref && props.showSEOText !== false && (
          <Link href={pricesHref}>
            <Button color="primary" size="small" fullWidth={false}>
              {t('viewPropertyPricesInLocation', {
                location: placeData.label,
              })}
            </Button>
          </Link>
        )}
      </Box>

      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fill, minmax(${
            place?.type === 'country' ? THREE_COLUMNS_WIDTH : TWO_COLUMNS_WIDTH
          }px, 1fr))`,
          gridGap: '16px',
        }}
      >
        {props.showOnlyPopularSearches !== true && (
          <>
            <Links
              title={getPlaceTitle(t, { ...placeData, rooms: null }, 'within')}
              items={alternatives.innerPlaces}
            />
            <Links
              title={getPlaceTitle(t, { ...placeData, rooms: null }, 'near')}
              items={alternatives.neighbouringPlaces}
            />
            <Links
              title={t('otherPropertyTypesInLocation', {
                inLocation: placeData.inLabel,
              })}
              items={alternatives.propertyTypes}
            />
          </>
        )}
        <Links
          title={
            descriptorState.offerType === 'buy'
              ? t('popularSearchesForSaleInLocation', {
                  inLocation: placeData.inLabel,
                })
              : t('popularSearchesForRentInLocation', {
                  inLocation: placeData.inLabel,
                })
          }
          items={popularSearchesItems}
        />
        {props.showOnlyPopularSearches !== true && (
          <Links
            title={getPlaceTitle(t, placeData, 'by_rooms')}
            items={alternatives.rooms}
          />
        )}
      </Box>
    </Box>
  );
};

export const AggregatesListingsSEOLinks: React.AbstractComponent<Props, mixed> =
  React.memo(
    AggregatesListingsSEOLinksInner,
    (prevProps, nextProps) =>
      prevProps.placeData.label === nextProps.placeData.label &&
      prevProps.placeData.inLabel === nextProps.placeData.inLabel &&
      prevProps.placeData.placeType === nextProps.placeData.placeType &&
      prevProps.placeData.propertyType === nextProps.placeData.propertyType &&
      prevProps.placeData.offerType === nextProps.placeData.offerType &&
      prevProps.placeData.rooms === nextProps.placeData.rooms &&
      prevProps.popularSearches === nextProps.popularSearches,
  );

const range = (from, to) =>
  Array.from(new Array(to - from + 1)).map((_, i) => i + from);
